import { assign, createMachine } from 'xstate'

import { Applicant, PublicApplicationData } from '@flatsy/types/Applications'
import { OrderApplicants } from '@flatsy/utils/Functions/ApplicationUtils/OrderApplicant'
import { Asset } from 'src/redux/slices/Assets'
import { Slot } from 'src/redux/slices/BookableSlots'
import { qualificationConfig } from 'src/redux/slices/QualificationConfig/Type'

import { isCoCandidat, isGuarantor } from './Conditions/ApplicantKind'
import { isAssetClosed } from './Conditions/Closed'
import { hasBooking } from './Conditions/HasBooking'
import { hasNoBookableSlot } from './Conditions/HasNoBookableSlot'
import { isAssetPro } from './Conditions/IsAssetPro'
import { isAssetSales } from './Conditions/IsAssetSale'
import { isLastApplicant } from './Conditions/IsLastApplicant'
import { isVerlingueSupportedGuarantor } from './Conditions/IsVerlingueGuarantor'
import { needsGuarantor } from './Conditions/NeedsGuarantor'
import { isBookingError, isUnqualified, isSuccess } from './Conditions/Result'
import { isInWaitingList } from './Conditions/WaitingList'
import { isFirstApplicant } from './Conditions/isFirstApplicant'
import { getTotalStep } from './Utils/Progress'

/**
 * @Troubleshooting XSTATE - TYPESCRIPT
 * @description typescript type error when using action like actions: assign({asset})
 * should use actions:
 *    assign((context, event) => {
 *      return {asset}
 *     }),
 * @link : https://xstate.js.org/docs/guides/typescript.html#assign-action-behaving-strangely
 */
export interface WizardNextEventParams {
  asset: Asset | undefined
  agentId: string | undefined
  application: PublicApplicationData | undefined
  applicants: Applicant[] | undefined
  qualification: qualificationConfig | undefined
  slots: Slot[] | undefined
  redirect?: boolean
  applicantId?: string
  bookingError: boolean
}

export type WizardEvent =
  | {
      type: 'INIT_RENT'
      value: WizardNextEventParams
    }
  | {
      type: 'INIT_SALE'
      value: WizardNextEventParams
    }
  | {
      type: 'INIT_PRO'
      value: WizardNextEventParams
    }
  | {
      type: 'INIT_MAIN_APPLICANT'
      value: WizardNextEventParams
    }
  | {
      type: 'INIT_CO_CANDIDAT'
      value: WizardNextEventParams
    }
  | {
      type: 'INIT_PROFESSIONAL'
      value: WizardNextEventParams
    }
  | {
      type: 'INIT_NEED_GUARANTOR'
      value: WizardNextEventParams
    }
  | {
      type: 'INIT_VERLINGUE_GUARANTOR'
      value: WizardNextEventParams
    }
  | {
      type: 'INIT_GUARANTOR'
      value: WizardNextEventParams
    }
  | {
      type: 'NEXT'
      value: WizardNextEventParams
    }
  | {
      type: 'BACK'
      value: WizardNextEventParams
    }

export interface WizardContext {
  asset: Asset | undefined
  agentId: string | undefined
  application: PublicApplicationData | undefined
  applicants: Applicant[] | undefined
  qualification: qualificationConfig | undefined
  slots: Slot[] | undefined
  index: number
  applicantId: string | undefined
  bookingError: boolean
  progress: {
    total: number
    current: number
  }
}

/**
 * CONDITION
 */

const updateState = (context: WizardContext, event: WizardEvent) => {
  const contextObject = { ...context }
  if (event.value.asset) {
    contextObject.asset = {
      ...context.asset,
      ...event.value.asset,
    }
  }
  if (event.value.agentId) {
    contextObject.agentId = event.value.agentId
  }
  if (event.value.qualification) {
    contextObject.qualification = {
      ...context.qualification,
      ...event.value.qualification,
    }
  }
  if (event.value.applicants) {
    contextObject.applicants = OrderApplicants(event.value.applicants)
    if (
      !event.value.applicantId &&
      !context.applicantId &&
      event.value.applicants &&
      event.value.applicants[contextObject.index]
    ) {
      contextObject.applicantId = event.value.applicants[contextObject.index]?.id
    }
  }
  if (event.value.applicantId) {
    if (event.value.applicants) {
      const index = event.value.applicants?.findIndex((el) => el.id === event.value.applicantId)

      if (index && index !== -1) {
        contextObject.index = index
      }
    }
    contextObject.applicantId = event.value.applicantId
  }

  if (event.value.application) {
    contextObject.application = {
      ...context.application,
      ...event.value.application,
    }
  }
  if (event.value.slots) {
    contextObject.slots = event.value.slots
  }
  if (event.value.bookingError) {
    contextObject.bookingError = event.value.bookingError
  }

  contextObject.progress = {
    total: getTotalStep(context, event),
    current: (contextObject.progress.current += 1),
  }

  return { ...contextObject }
}

const updateStateAndApplicantIndex = (context: WizardContext, event: WizardEvent) => {
  const contextObject = updateState(context, event)
  contextObject.index += 1
  if (contextObject.applicants && contextObject.applicants[contextObject.index]) {
    contextObject.applicantId = contextObject.applicants[contextObject.index]?.id
  }
  return { ...contextObject }
}

const updateStateAndApplicantIndexBack = (context: WizardContext, event: WizardEvent) => {
  const contextObject = updateState(context, event)
  contextObject.index -= 1
  if (contextObject.applicants && contextObject.applicants[contextObject.index]) {
    contextObject.applicantId = contextObject.applicants[contextObject.index]?.id
  }
  return { ...contextObject }
}

export const wizardMachine = createMachine<WizardContext, WizardEvent>({
  id: 'wizard',
  context: {
    asset: undefined,
    agentId: undefined,
    application: undefined,
    applicants: undefined,
    qualification: undefined,
    slots: undefined,
    index: 0,
    applicantId: undefined,
    bookingError: false,
    progress: {
      total: 0,
      current: 0,
    },
  },
  initial: 'idle',
  states: {
    /* -------------------------------------------------------------------------- */
    /*                                    IDLE                                    */
    /* -------------------------------------------------------------------------- */
    idle: {
      id: 'idle',
      on: {
        NEXT: [
          {
            target: '#closed',
            cond: isAssetClosed,
            actions: assign((context, event) => {
              return updateState(context, event)
            }),
          },
          {
            target: '#booking',
            actions: assign((context, event) => {
              return updateState(context, event)
            }),
          },
        ],
        INIT_RENT: {
          target: '#rent',
          actions: assign((context, event) => {
            return updateState(context, event)
          }),
        },
        INIT_SALE: {
          target: '#sale',
          actions: assign((context, event) => {
            return updateState(context, event)
          }),
        },
        INIT_PRO: {
          target: '#pro',
          actions: assign((context, event) => {
            return updateState(context, event)
          }),
        },
        INIT_MAIN_APPLICANT: {
          target: '#main_applicant',
          actions: assign((context, event) => {
            return updateState(context, event)
          }),
        },
        INIT_CO_CANDIDAT: {
          target: '#co_candidat',
          actions: assign((context, event) => {
            return updateState(context, event)
          }),
        },
        INIT_PROFESSIONAL: {
          target: '#professional',
          actions: assign((context, event) => {
            return updateState(context, event)
          }),
        },
        INIT_VERLINGUE_GUARANTOR: {
          target: '#verlingue_guarantor',
          actions: assign((context, event) => {
            return updateState(context, event)
          }),
        },
        INIT_NEED_GUARANTOR: {
          target: '#need_guarantor',
          actions: assign((context, event) => {
            return updateState(context, event)
          }),
        },
        INIT_GUARANTOR: {
          target: '#guarantor',
          actions: assign((context, event) => {
            return updateState(context, event)
          }),
        },
      },
    },
    /* -------------------------------------------------------------------------- */
    /*                                   BOOKING                                  */
    /* -------------------------------------------------------------------------- */
    booking: {
      id: 'booking',
      initial: 'booking_idle',
      states: {
        booking_idle: {
          id: 'booking_idle',
          on: {
            '': [
              {
                target: '#project',
                cond: (context, event) => hasBooking(context, event) || isInWaitingList(context),
              },
              {
                target: '#no_bookable_slot',
                cond: hasNoBookableSlot,
              },
              {
                target: '#booking_selection',
              },
            ],
          },
        },
        no_bookable_slot: {
          id: 'no_bookable_slot',
          on: {
            NEXT: [
              {
                target: '#project',
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
            ],
          },
        },
        booking_selection: {
          id: 'booking_selection',
          on: {
            NEXT: [
              {
                target: '#project',
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
            ],
          },
        },
      },
    },
    /* -------------------------------------------------------------------------- */
    /*                                   PROJECT                                  */
    /* -------------------------------------------------------------------------- */
    project: {
      id: 'project',
      initial: 'project_idle',
      states: {
        project_idle: {
          id: 'project_idle',
          on: {
            '': [
              {
                target: '#pro',
                cond: isAssetPro,
              },
              {
                target: '#sale',
                cond: isAssetSales,
              },
              { target: '#rent' },
            ],
          },
        },
        rent: {
          id: 'rent',
          on: {
            NEXT: [
              {
                target: '#main_applicant',
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
            ],
          },
        },
        sale: {
          id: 'sale',
          on: {
            NEXT: [
              {
                target: '#main_applicant',
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
            ],
          },
        },
        pro: {
          id: 'pro',
          on: {
            NEXT: [
              {
                target: '#main_applicant',
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
            ],
          },
        },
      },
    },
    /* -------------------------------------------------------------------------- */
    /*                                  APPLICANT                                 */
    /* -------------------------------------------------------------------------- */
    applicant: {
      // when many applicants
      id: 'applicant',
      initial: 'applicant_idle',
      states: {
        applicant_idle: {
          id: 'applicant_idle',
          on: {
            '': [
              {
                target: '#co_candidat',
                cond: isCoCandidat,
              },
              {
                target: '#verlingue_guarantor',
                cond: isVerlingueSupportedGuarantor,
              },
              {
                target: '#guarantor',
                cond: isGuarantor,
              },
              {
                target: '#main_applicant',
              },
            ],
          },
        },
        main_applicant: {
          id: 'main_applicant',
          on: {
            NEXT: [
              {
                target: '#end',
                cond: isAssetPro,
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
              {
                target: '#end',
                cond: isAssetSales,
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
              {
                target: '#professional',
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
            ],
          },
        },
        co_candidat: {
          id: 'co_candidat',
          on: {
            NEXT: {
              target: '#professional',
              actions: assign((context, event) => {
                return updateState(context, event)
              }),
            },
            BACK: [
              {
                target: '#project',
                cond: isFirstApplicant,
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
              {
                target: '#professional',
                actions: assign((context, event) => {
                  return updateStateAndApplicantIndexBack(context, event)
                }),
              },
            ],
          },
        },
        verlingue_guarantor: {
          id: 'verlingue_guarantor',
          on: {
            NEXT: [
              {
                target: '#professional',
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
              {
                target: '#applicant',
                actions: assign((context, event) => {
                  return updateStateAndApplicantIndex(context, event)
                }),
              },
            ],
            BACK: [
              {
                target: '#project',
                cond: isFirstApplicant,
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
              {
                target: '#professional',
                actions: assign((context, event) => {
                  return updateStateAndApplicantIndexBack(context, event)
                }),
              },
            ],
          },
        },
        professional: {
          id: 'professional',
          on: {
            NEXT: [
              {
                target: '#need_guarantor',
                cond: needsGuarantor,
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
              {
                target: '#end',
                cond: isLastApplicant,
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
              {
                target: '#applicant',
                actions: assign((context, event) => {
                  return updateStateAndApplicantIndex(context, event)
                }),
              },
            ],
            BACK: {
              target: '#applicant',
              actions: assign((context, event) => {
                return updateState(context, event)
              }),
            },
          },
        },
        need_guarantor: {
          id: 'need_guarantor',
          on: {
            NEXT: [
              {
                target: '#end',
                cond: isLastApplicant,
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
              {
                target: '#applicant',
                actions: assign((context, event) => {
                  return updateStateAndApplicantIndex(context, event)
                }),
              },
            ],
            BACK: [
              {
                target: '#project',
                cond: isFirstApplicant,
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
              {
                target: '#applicant',
                actions: assign((context, event) => {
                  return updateStateAndApplicantIndexBack(context, event)
                }),
              },
            ],
          },
        },
        guarantor: {
          id: 'guarantor',
          on: {
            NEXT: [
              {
                target: '#end',
                cond: isLastApplicant,
                actions: assign((context, event) => {
                  return updateState(context, event)
                }),
              },
              {
                target: '#applicant',
                actions: assign((context, event) => {
                  return updateStateAndApplicantIndex(context, event)
                }),
              },
            ],
            BACK: {
              target: '#need_guarantor',
              actions: assign((context, event) => {
                return updateStateAndApplicantIndexBack(context, event)
              }),
            },
          },
        },
      },
    },
    /* -------------------------------------------------------------------------- */
    /*                                   RESULT                                   */
    /* -------------------------------------------------------------------------- */
    end: {
      id: 'end',
      on: {
        NEXT: {
          target: '#result',
          actions: assign((context, event) => {
            return updateStateAndApplicantIndex(context, event)
          }),
        },
      },
    },
    /* -------------------------------------------------------------------------- */
    /*                                   RESULT                                   */
    /* -------------------------------------------------------------------------- */
    result: {
      id: 'result',
      initial: 'result_idle',
      states: {
        result_idle: {
          id: 'result_idle',
          on: {
            '': [
              {
                target: '#refused',
                cond: isUnqualified,
              },
              {
                target: '#slot_already_taken',
                cond: isBookingError,
              },
              {
                target: '#success',
                cond: isSuccess,
              },
            ],
          },
        },
        success: {
          id: 'success',
          type: 'final',
        },
        slot_already_taken: {
          id: 'slot_already_taken',
          type: 'final',
        },
        refused: {
          id: 'refused',
          type: 'final',
        },
      },
    },
    /* -------------------------------------------------------------------------- */
    /*                                   CLOSED                                   */
    /* -------------------------------------------------------------------------- */
    closed: {
      id: 'closed',
      type: 'final',
    },
  },
})
